import React, { useMemo } from "react";
import { Navigate, useParams } from "react-router-dom";
import { MapPin, Home, ShieldCheck, TrendingUp, Car, Video } from "lucide-react";
import LandingLayout, { useLandingMeta } from "@/components/landing/LandingLayout";
import data from "@/lib/landing.json";

const SITE = "https://mdcourtage.fr";

export const cityFaq = (c) => [
  [`Vous déplacez-vous à ${c.name} ?`, `Oui. J'interviens à ${c.name} (${c.cp}) et dans les communes voisines (${c.nearby.slice(0, 4).join(", ")}…) : je viens chez vous, sur votre lieu de travail, ou nous échangeons en visio.`],
  [`Combien coûte un courtier à ${c.name} ?`, "En crédit, les frais de courtage sont fixés dans le mandat et dus uniquement après le déblocage des fonds. En assurance : 25 € (1 contrat), 40 € (2) ou 50 € (3 et plus). En placement, aucun honoraire n'est facturé au client. Le premier rendez-vous est offert."],
  ["Quels sont les délais pour un crédit immobilier ?", "Comptez en général 3 à 6 semaines entre le dépôt d'un dossier complet et l'émission de l'offre de prêt, puis un délai de réflexion légal de 10 jours avant de l'accepter."],
];

const SERVICES = [
  { icon: Home, title: "Crédit immobilier", desc: "Résidence principale, investissement locatif, rachat de crédits, prêt professionnel." },
  { icon: ShieldCheck, title: "Assurance", desc: "Assurance emprunteur, prévoyance, santé, habitation, auto, RC pro, homme-clé." },
  { icon: TrendingUp, title: "Placement", desc: "Assurance-vie, PER, SCPI, défiscalisation, préparation de la retraite et transmission." },
];

export default function CityPage() {
  const { city } = useParams();
  const c = data.cities.find((x) => x.slug === city);
  const meta = useMemo(() => c && {
    title: `Courtier à ${c.name} (${c.cp}) — crédit, assurance, placement | MD Courtage`,
    description: `Courtier en crédit immobilier, assurance et placement à ${c.name} (${c.cp}) et alentours. Déplacement à domicile ou visio, premier rendez-vous offert.`,
    jsonLd: [
      {
        "@context": "https://schema.org", "@type": "FinancialService", name: `MD Courtage — courtier à ${c.name}`,
        url: `${SITE}/courtier/${c.slug}`, telephone: "+33688073833", email: "contact@mdcourtage.fr",
        areaServed: [{ "@type": "City", name: c.name, postalCode: c.cp }, ...c.nearby.map((n) => ({ "@type": "City", name: n }))],
        founder: { "@type": "Person", name: "Maël DUBOIS" },
      },
      { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: cityFaq(c).map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
    ],
  }, [c]);
  useLandingMeta(meta || { title: "MD Courtage", description: "", jsonLd: {} });
  if (!c) return <Navigate to="/" replace />;

  return (
    <LandingLayout testId={`city-page-${c.slug}`} page={`/courtier/${c.slug}`}>
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-12 md:pt-24 grid lg:grid-cols-12 gap-12 animate-fade-up">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-md-accent font-bold mb-5"><MapPin className="w-4 h-4" /> {c.name} · {c.cp} · {c.dept}</div>
          <h1 data-testid="city-h1" className="font-serif text-4xl sm:text-5xl lg:text-6xl text-md-primary font-medium leading-[1.05] mb-6">
            Votre courtier à <span className="italic">{c.name}</span>
          </h1>
          <p className="text-lg text-md-text/80 leading-relaxed mb-5">{c.intro}</p>
          <p className="text-md-muted leading-relaxed mb-8">{c.angle}</p>
          <div className="flex flex-wrap gap-3">
            <a href="#devis" data-testid="city-cta-quote" className="bg-md-secondary hover:bg-md-secondaryHover text-white px-7 py-3.5 rounded-full font-semibold shadow-lg shadow-md-secondary/30 transition-transform hover:-translate-y-0.5">Demander un devis gratuit</a>
            <span className="inline-flex items-center gap-2 text-sm text-md-primary px-4"><Car className="w-4 h-4" /> À domicile <Video className="w-4 h-4 ml-2" /> ou en visio</span>
          </div>
        </div>
        <aside className="lg:col-span-5 bg-white border border-md-border rounded-3xl p-8 self-start" data-testid="city-nearby">
          <div className="text-[10px] uppercase tracking-widest text-md-muted font-bold mb-3">Communes desservies autour de {c.name}</div>
          <div className="flex flex-wrap gap-2">
            {[c.name, ...c.nearby].map((n) => <span key={n} className="text-sm bg-md-bg text-md-primary px-3 py-1.5 rounded-full">{n}</span>)}
          </div>
        </aside>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16 grid md:grid-cols-3 gap-5">
        {SERVICES.map(({ icon: I, title, desc }) => (
          <div key={title} className="bg-white border border-md-border rounded-3xl p-7">
            <I className="w-7 h-7 text-md-secondary mb-4" />
            <h2 className="font-serif text-2xl text-md-primary font-medium mb-2">{title} à {c.name}</h2>
            <p className="text-sm text-md-muted leading-relaxed">{desc}</p>
          </div>
        ))}
      </section>

      <section className="max-w-4xl mx-auto px-6 md:px-12 pb-20" data-testid="city-faq">
        <h2 className="font-serif text-3xl text-md-primary font-medium mb-6">Questions fréquentes — {c.name}</h2>
        <div className="space-y-4">
          {cityFaq(c).map(([q, a]) => (
            <details key={q} className="bg-white border border-md-border rounded-2xl p-5 group">
              <summary className="font-semibold text-md-primary cursor-pointer">{q}</summary>
              <p className="text-sm text-md-muted leading-relaxed mt-3">{a}</p>
            </details>
          ))}
        </div>
        <p className="text-xs text-md-muted mt-6">Un crédit vous engage et doit être remboursé. Vérifiez vos capacités de remboursement avant de vous engager. Les placements présentent un risque de perte en capital.</p>
      </section>
    </LandingLayout>
  );
}
